// src/utils/adminAuth.js
import { ethers } from 'ethers';

/**
 * Generate authentication headers for admin API requests
 * Requires connected wallet with signing capability
 *
 * @param {ethers.Signer} signer - Ethers signer from connected wallet
 * @returns {Promise<Object>} Headers object for fetch requests
 */
export async function generateAdminAuthHeaders(signer) {
  if (!signer) {
    throw new Error('Wallet signer required for admin authentication');
  }

  const address = await signer.getAddress();
  const timestamp = Date.now().toString();
  const nonce = `${timestamp}-${Math.random().toString(36).substring(2, 15)}`;
  const message = `Admin Action: ${timestamp}:${nonce}`;

  // Sign the message using EIP-191
  const signature = await signer.signMessage(message);

  return {
    'x-admin-address': address,
    'x-admin-signature': signature,
    'x-admin-message': message,
    'x-admin-timestamp': timestamp,
    'x-admin-nonce': nonce,
    'Content-Type': 'application/json',
  };
}

/**
 * Get ethers signer from window.ethereum
 * @returns {Promise<ethers.Signer|null>}
 */
export async function getWalletSigner() {
  if (typeof window === 'undefined' || !window.ethereum) {
    console.error('No wallet provider found');
    return null;
  }

  try {
    // Request account access if needed
    await window.ethereum.request({ method: 'eth_requestAccounts' });

    const provider = new ethers.BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();
    return signer;
  } catch (error) {
    console.error('Failed to get wallet signer:', error);
    return null;
  }
}

/**
 * Make an authenticated admin API request
 *
 * @param {string} url - API endpoint URL
 * @param {Object} options - Fetch options (method, body, etc.)
 * @returns {Promise<Response>}
 */
export async function adminFetch(url, options = {}) {
  const signer = await getWalletSigner();

  if (!signer) {
    throw new Error('Please connect your wallet to perform admin actions');
  }

  const authHeaders = await generateAdminAuthHeaders(signer);

  const response = await fetch(url, {
    ...options,
    headers: {
      ...authHeaders,
      ...options.headers,
    },
  });

  return response;
}

/**
 * Check if current wallet is admin
 * @param {string} address - Wallet address to check
 * @returns {Promise<boolean>}
 */
export async function isAdmin(address) {
  if (!address) return false;

  try {
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002';
    const response = await fetch(`${API_BASE_URL}/api/admin/check?address=${address}`);

    if (response.ok) {
      const data = await response.json();
      return data.isAdmin === true;
    }
    return false;
  } catch (error) {
    console.error('Failed to check admin status:', error);
    return false;
  }
}

export default {
  generateAdminAuthHeaders,
  getWalletSigner,
  adminFetch,
  isAdmin,
};
